// eslint-disable-next-line max-len
import { Component, Input, OnInit, AfterViewInit, OnDestroy, SimpleChanges, OnChanges, ElementRef, EventEmitter, Output, ViewChild } from '@angular/core';
import { GetOrderNumQuotaListService } from './api-services/get-order-num-quota-list.service';
import { OrderNumQuotaCodeComboBoxModel } from './order-num-quota-code-combo-box.model';
import { PlessHttp } from '../../../../shared/http/pless-http.service';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { PlessDialogServiceSub } from '../../../../commons/kymw00/pless-dialog-service-sub';
import { PlessViewUtilsSub } from '../../../../commons/kymw00/pless-view-utils-sub.service';
import { OrderNumQuota } from './order-num-quota.model';

/**
 * 共通部品 注文コード割当コードコンボボックスを制御するクラス
 *
 * @export OrderNumQuotaComboBoxComponent
 * @class OrderNumQuotaComboBoxComponent
 * @implements {OnInit}
 * @implements {AfterViewInit}
 * @implements {OnChanges}
 * @implements {OnDestroy}
 */
@Component({
  selector: 'app-order-num-quota-code-combo-box',
  templateUrl: 'order-num-quota-code-combo-box.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: OrderNumQuotaCodeComboBoxComponent,
      multi: true
    },
    {
      provide: GetOrderNumQuotaListService,
      useFactory: (plessHttp: PlessHttp, dialogService: PlessDialogServiceSub) =>
        new GetOrderNumQuotaListService(plessHttp, dialogService),
        deps: [PlessHttp, PlessDialogServiceSub]
      }
  ],
})

export class OrderNumQuotaCodeComboBoxComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy, ControlValueAccessor {

  public selectedIndex: number = <number>-1;
  public selectedOrderNumQuotaCode: string = <string>'';
  public selectedOrderNumQuotaName: string = <string>'';
  public selectedDelivCatalogID: string = <string>'';
  public selectedDeliveryWeekFixFlag: string = <string>'';

  public model: OrderNumQuotaCodeComboBoxModel = new OrderNumQuotaCodeComboBoxModel();

  @ViewChild('comboBox', { static: true }) public comboBox: ElementRef;

  @Input() public subid: string = null;
  @Input() blank: boolean = <boolean>true;                                      // 空白フラグ
  @Input() initAutoLoad: boolean = <boolean>true;                               // オートロードフラグ
  @Input() labelField: string = <string>'OrderNumQuotaName';

  @Input() orderCode: string = <string>'';                                      // 受注コード(voの値)
  @Input() mediumType: string = <string>'';                                     // 媒体区分(voの値)
  @Input() multiPublishFlg: string = <string>'0';                               // Ｗ掲載商品集計フラグ(0：未選択、1：選択)(voの値)
  @Input() tabindex: number = null;
  @Input() forcedEnabledMode: string = null;

  // レイアウト系の変数
  @Input() left: number = <number>0;
  @Input() top: number = <number>0;
  @Input() width: number | string;
  @Input() height: number = <number>20;
  @Input() disabled: boolean = <boolean>false;
  @Input() required: boolean = <boolean>false;
  @Input() initFocusItem: any = null;                                           // ロード完了後フォーカスを移動させるid
  @Input() afterFocus: string;                                                  // ロード完了後フォーカスを移動させるid
  @Input() visible: boolean = <boolean>true;                                    // 表示・非表示フラグ

  @Output() loadCompleted: EventEmitter<any> = new EventEmitter<any>();         // ロード完了通知


  /**
   * Creates an instance of orderNumQuotaCodeComboBox.
   * @memberof orderNumQuotaCodeComboBox
   * @param {getOrderNumQuotaListService} GetOrderNumQuotaListService コードリストを取得する
   */
  constructor(
    public elementRef: ElementRef,
    private getOrderNumQuotaListService: GetOrderNumQuotaListService,
  ) { }


  // ValueAccessor
  public onChange(_: any) { }
  public onTouched() { }
  public writeValue(text: string): void {
    if (text !== this.selectedOrderNumQuotaCode) {
      this.selectedOrderNumQuotaCode = text;
      this.setOrderNumQuotaCode(text);
    }
  }
  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * 初期化処理
   *
   * @memberof OrderNumQuotaCodeComboBoxComponent
   */
  ngOnInit(): void {
    // コンボボックスインスタンス設定
    this.model.component = this;
    this.comboBox.nativeElement.setAttribute('id', this.subid + '_sub');
    this.comboBox.nativeElement.setAttribute('forcedEnabledMode', this.forcedEnabledMode);
  }

  /**
   * ロード完了時
   *
   * @memberof OrderNumQuotaCodeComboBoxComponent
   */
  ngAfterViewInit(): void {
    if (this.model.initAutoLoad) {
      // データ取得
      this.reload();
    }
  }

  /**
   * 値変更時イベント
   *
   * @param {SimpleChanges} changes
   * @memberof OrderNumQuotaCodeComboBoxComponent
   */
  ngOnChanges(changes: SimpleChanges): void {
    Object.keys(changes).forEach((propName) => {
      switch (propName) {
        case 'blank':
          // 先頭空白表示フラグ
          this.model.blank = changes.blank.currentValue;
          break;
        case 'initAutoLoad':
          // 初期データ自動取得フラグ
          this.model.initAutoLoad = changes.initAutoLoad.currentValue;
          break;
        case 'orderCode':
          // 検索条件「受注コード」を設定するフラグ
          this.orderCode = changes.orderCode.currentValue;
          this.model.orderCode = changes.orderCode.currentValue;
          this.selectedIndex = 0;
          this.reload();
          break;
        case 'mediumType':
          // 検索条件「媒体区分」を設定するフラグ
          this.mediumType = changes.mediumType.currentValue;
          this.model.mediumType = changes.mediumType.currentValue;
          this.selectedIndex = 0;
          this.reload();
          break;
        case 'multiPublishFlg':
          // 検索条件「Ｗ掲載商品集計フラグ」を設定するフラグ
          this.multiPublishFlg = changes.multiPublishFlg.currentValue;
          this.model.multiPublishFlg = changes.multiPublishFlg.currentValue;
          this.selectedIndex = 0;
          this.reload();
          break;
      }
    });
  }

  /**
   * コンポーネント終了時
   *
   * @memberof OrderNumQuotaCodeComboBoxComponent
   */
  ngOnDestroy(): void {
    // コンボボックス情報初期化
    this.clear();
  }

  /**
   * クリア
   *
   * @memberof OrderNumQuotaCodeComboBoxComponent
   */
  public clear(): void {
    // コンボボックス情報初期化
    this.selectedIndex = <number>-1;
    this.selectedOrderNumQuotaCode = <string>'';
    this.selectedOrderNumQuotaName = <string>'';
    this.selectedDelivCatalogID = <string>'';
    this.selectedDeliveryWeekFixFlag = <string>'';
    this.model = new OrderNumQuotaCodeComboBoxModel();
    this.model.component = this;
    this.model.blank = this.blank;
  }

  /**
   * コンボボックスの選択媒体Nameを切り替える
   *
   * @param {string} orderNumQuotaCode 注文コード割当コード
   * @memberof OrderNumQuotaCodeComboBoxComponent
   */
  public setOrderNumQuotaCode(orderNumQuotaCode: string): void {
    this.model.setOrderNumQuotaCode = orderNumQuotaCode;
    // ロード完了の場合
    if (this.model.completeUpdateFlg) {
      this.updateSelectedOrderNumQuotaCode();
    }
  }

  /**
   * コンボボックスの選択コードNameを切り替える
   *
   * @memberof OrderNumQuotaCodeComboBoxComponent
   */
  public updateSelectedOrderNumQuotaCode(): void {
    let tempList: OrderNumQuota[] = this.model.orderNumQuotaList;
    if (PlessViewUtilsSub.isEmptyArray(tempList)) {
      tempList = [];
    }
    for (let i = Number(0); i < tempList.length; i++) {
      if (PlessViewUtilsSub.trim(<string>tempList[i].OrderNumQuotaCode) === this.model.setOrderNumQuotaCode) {
        this.selectedIndex = i;
      }
    }

    // バインドフィールドを更新
    this.updateBindedField(null);
  }

  /**
   * コンボボックスの内容を再度ロードする
   *
   * @returns {void}
   * @memberof OrderNumQuotaCodeComboBoxComponent
   */
  public reload(): void {
    // 受注コード、媒体区分のチェック
    if (PlessViewUtilsSub.isEmpty(this.orderCode) ||                            // 未指定、
      PlessViewUtilsSub.isEmpty(this.mediumType)) {                           // 未指定の場合
      this.model.completeUpdateFlg = true;
      // ロード完了通知
      this.loadCompleted.emit(this.selectedOrderNumQuotaCode);

      // 処理復帰
      return;
    }

    // データ取得
    this.getData();
  }

  /**
   * バインドフィールド初期化
   *
   * @private
   * @param {*} e
   * @returns {void}
   * @memberof OrderNumQuotaCodeComboBoxComponent
   */
  public updateBindedField(e: any): void {
    try {
      if (e != null) {
        this.selectedIndex = e.target.selectedIndex;
      }
      if (this.selectedIndex === -1) {
        this.selectedOrderNumQuotaCode = <string>'';
        this.selectedOrderNumQuotaName = <string>'';
        this.selectedDelivCatalogID = <string>'';
        this.selectedDeliveryWeekFixFlag = <string>'';
        return;
      }
      if (PlessViewUtilsSub.isEmptyArray(this.model.orderNumQuotaList)) {
        this.selectedIndex = -1;
        this.selectedOrderNumQuotaCode = <string>'';
        this.selectedOrderNumQuotaName = <string>'';
        this.selectedDelivCatalogID = <string>'';
        this.selectedDeliveryWeekFixFlag = <string>'';
        return;
      }
      this.selectedOrderNumQuotaCode =
        this.model.orderNumQuotaList[this.selectedIndex].OrderNumQuotaCode;
      this.selectedOrderNumQuotaName =
        this.model.orderNumQuotaList[this.selectedIndex].OrderNumQuotaName;
      this.selectedDelivCatalogID =
        this.model.orderNumQuotaList[this.selectedIndex].DelivCatalogID;
      this.selectedDeliveryWeekFixFlag =
        this.model.orderNumQuotaList[this.selectedIndex].DeliveryWeekFixFlag;
    } finally {
      // ロード完了通知
      this.loadCompleted.emit(this.selectedOrderNumQuotaCode);
    }
  }

  /**
   * 強制的にバインドフィールド更新
   *
   * @memberof OrderNumQuotaCodeComboBoxComponent
   */
  public update() {
    this.updateBindedField(null);
  }

  /**
   * データ取得
   *
   * @memberof OrderNumQuotaCodeComboBoxComponent
   */
  public getData(): void {
    this.getOrderNumQuotaListService.execute(this.model, this.orderCode, this.mediumType, this.multiPublishFlg);
  }

}
